import { useEffect, useMemo, useState } from 'react';

import { Button, Center, Flex, FormControl, FormLabel, Input, Spinner } from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { CheckOutline, useToast } from '@aslanpay/portal-common-lib';

import { ToggleRow } from 'app/components/Settings/ToggleRow';

import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { FileUploadFormState, FileUploadTabProps } from './FileUploadTab.types';

function FileUploadTab({ companyId = '' }: FileUploadTabProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: companyConfiguration, isInitialLoading } = useFetchCompanyConfiguration(companyId);

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const [isSaving, setIsSaving] = useState(false);

  const initialForm = useMemo<FileUploadFormState>(() => {
    const config = companyConfiguration?.fileUploadConfig;
    return {
      leaversFileUploadEnabled: config?.leaversFileUploadEnabled ?? false,
      inviteNewEmployeesAutomatically: config?.inviteNewEmployeesAutomatically ?? false,
      sftpUploadDirectory: config?.sftpUploadDirectory ?? '',
      sftpAutomationEdfEnabled: config?.sftpAutomationEdfEnabled ?? false,
      sftpAutomationElfEnabled: config?.sftpAutomationElfEnabled ?? false,
      sftpAutomationAetEnabled: config?.sftpAutomationAetEnabled ?? false,
    };
  }, [companyConfiguration]);

  const [form, setForm] = useState<FileUploadFormState>(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const updateField = <Key extends keyof FileUploadFormState>(
    key: Key,
    value: FileUploadFormState[Key]
  ) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const hasChanged = useMemo(
    () =>
      (Object.keys(initialForm) as (keyof FileUploadFormState)[]).some(
        (key) => form[key] !== initialForm[key]
      ),
    [form, initialForm]
  );

  const isSaveDisabled = !hasChanged || isSaving;

  const handleSave = async () => {
    if (!companyConfiguration) return;

    setIsSaving(true);
    try {
      await updateCompanyConfigurationAsync({
        id: companyConfiguration.companyId,
        fileUploadConfig: {
          leaversFileUploadEnabled: form.leaversFileUploadEnabled,
          inviteNewEmployeesAutomatically: form.inviteNewEmployeesAutomatically,
          sftpUploadDirectory: form.sftpUploadDirectory.trim(),
          sftpAutomationEdfEnabled: form.sftpAutomationEdfEnabled,
          sftpAutomationElfEnabled: form.sftpAutomationElfEnabled,
          sftpAutomationAetEnabled: form.sftpAutomationAetEnabled,
        },
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The file upload configuration was successfully saved.',
      });

      queryClient.invalidateQueries({
        queryKey: ['fetchCompanyConfiguration', companyConfiguration.companyId],
      });
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the file upload configuration.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isInitialLoading) {
    return (
      <Center width="full" py="40px">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  return (
    <Flex flexDir="column" gap="20px" alignItems="stretch" w="full">
      <ProfileSection title="File uploads" isEditMode maxW="100%">
        <ToggleRow
          label="Leavers file uploads"
          description="Allow leavers (ELF) files to be uploaded in the client portal."
          isChecked={form.leaversFileUploadEnabled}
          onChange={(checked) => updateField('leaversFileUploadEnabled', checked)}
          testId="leavers-file-upload-switch"
        />
        <ToggleRow
          label="Auto-invite new employees"
          description="Automatically invite new employees added through a file upload."
          isChecked={form.inviteNewEmployeesAutomatically}
          onChange={(checked) => updateField('inviteNewEmployeesAutomatically', checked)}
          testId="auto-invite-employees-switch"
        />
      </ProfileSection>

      <ProfileSection title="SFTP automation" isEditMode maxW="100%">
        <FormControl>
          <FormLabel>SFTP upload directory</FormLabel>
          <Input
            type="text"
            placeholder="Enter SFTP upload directory"
            value={form.sftpUploadDirectory}
            onChange={(event) => updateField('sftpUploadDirectory', event.target.value)}
            data-testid="sftp-upload-directory-input"
          />
        </FormControl>

        <ToggleRow
          label="Automate Employee data file (EDF)"
          description="Automatically process EDF files dropped in the SFTP directory."
          isChecked={form.sftpAutomationEdfEnabled}
          onChange={(checked) => updateField('sftpAutomationEdfEnabled', checked)}
          testId="sftp-automation-edf-switch"
        />
        <ToggleRow
          label="Automate Leavers file (ELF)"
          description="Automatically process ELF files dropped in the SFTP directory."
          isChecked={form.sftpAutomationElfEnabled}
          onChange={(checked) => updateField('sftpAutomationElfEnabled', checked)}
          testId="sftp-automation-elf-switch"
        />
        <ToggleRow
          label="Automate Timesheet (AET)"
          description="Automatically process AET (approved earnings) files dropped in the SFTP directory."
          isChecked={form.sftpAutomationAetEnabled}
          onChange={(checked) => updateField('sftpAutomationAetEnabled', checked)}
          testId="sftp-automation-aet-switch"
        />
      </ProfileSection>

      <Button
        leftIcon={<CheckOutline boxSize="20px" />}
        size="sm"
        alignSelf="flex-start"
        onClick={handleSave}
        isLoading={isSaving}
        isDisabled={isSaveDisabled}
        data-testid="save-file-upload-config-button"
      >
        Save changes
      </Button>
    </Flex>
  );
}

export { FileUploadTab };
